// The mock-up's list panel body: rows load 25 at a time as you scroll, under a
// select-all / sort bar, with a "Showing x of y" footer. A second, optional
// table view shows the same records as a wide grid.
//
// Rendering only — it never fetches, filters or sorts; the page hands it the
// list it has already worked out, and a `renderRow` that turns one record into
// the row's pieces.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { AxAvatar, AxEditIcon, AxTrashIcon } from "./adminUi";

const DEFAULT_BATCH = 25;

const cellValue = (record, dataIndex) => {
  if (record == null || dataIndex == null) return undefined;
  const path = Array.isArray(dataIndex) ? dataIndex : String(dataIndex).split(".");
  return path.reduce((acc, k) => (acc == null ? acc : acc[k]), record);
};

const AdminList = ({
  items = [],
  keyOf = (r, i) => r?.guid ?? r?.id ?? i,
  selectedKey,
  onSelect,
  // selection checkboxes — omit `picked` to hide them
  picked,
  onPick,
  onPickAll,
  // one record -> { name, tags, sub, right, inactive }
  renderRow,
  // sort bar
  sorts,
  sort,
  onSort,
  // table view
  table = false,
  columns,
  onRowEdit,
  onRowDelete,
  // chrome
  bulkBar,
  batch = DEFAULT_BATCH,
  emptyTitle = "Nothing matches",
  emptyHint = "Try a shorter search, or clear a filter.",
  footNote,
}) => {
  const [shown, setShown] = useState(batch);
  const listRef = useRef(null);
  const sentinelRef = useRef(null);

  // A new result set starts from the first batch again. Keyed on what the rows
  // are rather than on the array's identity: the page rebuilds its filtered
  // array on every render, and re-reading it must not throw away batches the
  // user already scrolled past.
  const signature = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    const edge = (r) => (r == null ? "" : r.guid ?? r.id ?? "");
    return list.length + "|" + edge(list[0]) + "|" + edge(list[list.length - 1]);
  }, [items]);

  useEffect(() => {
    setShown(batch);
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [signature, batch, table]);

  useEffect(() => {
    const root = listRef.current;
    const target = sentinelRef.current;
    if (!root || !target || typeof IntersectionObserver === "undefined") return;
    if (shown >= items.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setShown((n) => n + batch);
      },
      { root, rootMargin: "220px" }
    );
    io.observe(target);
    return () => io.disconnect();
  }, [shown, items.length, batch]);

  const visible = items.slice(0, shown);
  const hasPick = !!picked && typeof onPick === "function";
  const allPicked = hasPick && items.length > 0 && items.every((r, i) => picked[keyOf(r, i)]);

  const arrow = (key) => (sort && sort.key === key ? (sort.dir > 0 ? "▲" : "▼") : "");

  return (
    <>
      {/* ---- select all + sort ---- */}
      {sorts && !bulkBar ? (
        <div className="axa-lbar">
          {hasPick && (
            <input
              type="checkbox"
              checked={allPicked}
              onChange={(e) => onPickAll && onPickAll(e.target.checked)}
              aria-label="Select all"
            />
          )}
          {sorts.map((s, i) => (
            <React.Fragment key={s.key}>
              {i === 1 ? <span className="sp" /> : null}
              <button type="button" onClick={() => onSort && onSort(s.key)}>
                {s.label} <span>{arrow(s.key)}</span>
              </button>
            </React.Fragment>
          ))}
        </div>
      ) : null}

      {bulkBar}

      {/* ---- rows ---- */}
      {!table ? (
        <div className="axa-list" ref={listRef}>
          {visible.map((record, i) => {
            const key = keyOf(record, i);
            const r = renderRow(record, i) || {};
            return (
              <div
                key={key}
                className={
                  "axa-row" +
                  (hasPick ? "" : " nopick") +
                  (key === selectedKey ? " sel" : "") +
                  (r.inactive ? " inactive" : "")
                }
                role={onSelect ? "button" : undefined}
                tabIndex={onSelect ? 0 : undefined}
                onClick={onSelect ? () => onSelect(record) : undefined}
                onKeyDown={
                  onSelect
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onSelect(record);
                        }
                      }
                    : undefined
                }
              >
                {hasPick && (
                  <span className="axa-pick" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={!!picked[key]}
                      onChange={(e) => onPick(key, e.target.checked, record)}
                      aria-label={"Select " + (r.name || "")}
                    />
                  </span>
                )}
                <AxAvatar name={r.avatarName || r.name} />
                <span className="axa-rmain">
                  <span className="axa-rname">
                    <b>{r.name}</b>
                    {r.tags}
                  </span>
                  {r.sub ? <span className="axa-rsub">{r.sub}</span> : null}
                </span>
                <span className="axa-rright">{r.right}</span>
              </div>
            );
          })}

          <div className="axa-sentinel" ref={sentinelRef} />

          {!items.length && (
            <div className="axa-noresult">
              <div className="big">{emptyTitle}</div>
              <div>{emptyHint}</div>
            </div>
          )}
        </div>
      ) : (
        /* ---- table view ---- */
        <div className="axa-tablewrap" ref={listRef}>
          <table className="axa-ut">
            <thead>
              <tr>
                {hasPick && (
                  <th className="chk">
                    <input
                      type="checkbox"
                      checked={allPicked}
                      onChange={(e) => onPickAll && onPickAll(e.target.checked)}
                      aria-label="Select all"
                    />
                  </th>
                )}
                {(columns || []).map((c, i) => (
                  <th key={c.key || c.dataIndex || i}>
                    {c.sortKey && onSort ? (
                      <button type="button" onClick={() => onSort(c.sortKey)}>
                        {c.title} <span>{arrow(c.sortKey)}</span>
                      </button>
                    ) : (
                      <button type="button" tabIndex={-1} style={{ cursor: "default" }}>
                        {c.title}
                      </button>
                    )}
                  </th>
                ))}
                {(onRowEdit || onRowDelete) && <th style={{ width: 96 }} />}
              </tr>
            </thead>
            <tbody>
              {visible.map((record, i) => {
                const key = keyOf(record, i);
                return (
                  <tr
                    key={key}
                    className={key === selectedKey ? "sel" : ""}
                    onClick={onSelect ? () => onSelect(record) : undefined}
                    style={onSelect ? { cursor: "pointer" } : undefined}
                  >
                    {hasPick && (
                      <td className="chk" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={!!picked[key]}
                          onChange={(e) => onPick(key, e.target.checked, record)}
                          aria-label="Select row"
                        />
                      </td>
                    )}
                    {(columns || []).map((c, ci) => (
                      <td key={c.key || c.dataIndex || ci} className={c.mono ? "mono" : ""}>
                        {typeof c.render === "function"
                          ? c.render(cellValue(record, c.dataIndex), record, i)
                          : cellValue(record, c.dataIndex) ?? "—"}
                      </td>
                    ))}
                    {(onRowEdit || onRowDelete) && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="axa-rowacts">
                          {onRowEdit && (
                            <button
                              type="button"
                              className="axa-rowbtn"
                              title="Open"
                              aria-label="Open"
                              onClick={() => onRowEdit(record)}
                            >
                              <AxEditIcon />
                            </button>
                          )}
                          {onRowDelete && (
                            <button
                              type="button"
                              className="axa-rowbtn del"
                              title="Delete"
                              aria-label="Delete"
                              onClick={() => onRowDelete(record)}
                            >
                              <AxTrashIcon />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="axa-sentinel" ref={sentinelRef} />

          {!items.length && (
            <div className="axa-noresult">
              <div className="big">{emptyTitle}</div>
              <div>{emptyHint}</div>
            </div>
          )}
        </div>
      )}

      {/* ---- footer ---- */}
      <div className="axa-lfoot">
        <span>
          Showing <b>{visible.length}</b> of <b>{items.length}</b>
          {footNote ? " · " + footNote : ""}
        </span>
        <span className="mid">
          {shown < items.length ? (
            <button
              type="button"
              className="axa-loadbtn"
              onClick={() => setShown((n) => n + batch)}
            >
              Load {Math.min(batch, items.length - shown)} more
            </button>
          ) : null}
        </span>
      </div>
    </>
  );
};

export default AdminList;
